/*
 * Seletor de kit (snippets/kit-selector.liquid)
 * Cada card escolhe um produto (cor) e as opções dele (tamanhos).
 * O botão adiciona as variantes escolhidas ao carrinho, marcadas com o nome do kit.
 */
if (!customElements.get('kit-selector')) {
  class KitCard {
    constructor(el, onChange) {
      this.el = el;
      this.onChange = onChange;
      this.products = JSON.parse(el.querySelector('[data-kit-data]').textContent);
      this.image = el.querySelector('[data-kit-image]');
      this.colorName = el.querySelector('[data-kit-color-name]');
      this.swatches = el.querySelector('[data-kit-swatches]');
      this.optionsEl = el.querySelector('[data-kit-options]');
      this.selected = [];

      const first = this.products.find((p) => p.available) || this.products[0];
      this.renderSwatches();
      this.selectProduct(first);
    }

    renderSwatches() {
      if (this.products.length < 2) {
        this.swatches.hidden = true;
      }
      this.products.forEach((product) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'kit-card__swatch';
        btn.title = product.color;
        btn.setAttribute('aria-label', product.color);
        btn.dataset.productId = product.id;
        if (product.hex) btn.style.setProperty('--swatch-color', product.hex);
        if (!product.available) {
          btn.classList.add('is-disabled');
          btn.disabled = true;
        }
        btn.addEventListener('click', () => this.selectProduct(product));
        this.swatches.appendChild(btn);
      });
    }

    selectProduct(product) {
      const previous = this.product;
      this.product = product;

      // Mantém os tamanhos já escolhidos se a nova cor tiver as mesmas opções
      const keep = previous && previous.options.join('|') === product.options.join('|');
      this.selected = keep ? this.selected.slice() : product.options.map(() => null);

      this.swatches.querySelectorAll('.kit-card__swatch').forEach((btn) => {
        btn.classList.toggle('is-active', Number(btn.dataset.productId) === product.id);
      });
      this.colorName.textContent = product.color;
      if (product.image) {
        this.image.src = product.image;
        this.image.alt = product.title;
      }

      this.renderOptions();
      // Descarta escolhas que ficaram indisponíveis na nova cor
      this.selected = this.selected.map((value, i) => (value && this.isValueAvailable(i, value) ? value : null));
      this.updateOptionStates();
      this.onChange();
    }

    renderOptions() {
      this.optionsEl.innerHTML = '';
      this.product.options.forEach((name, index) => {
        const values = [];
        this.product.variants.forEach((v) => {
          if (!values.includes(v.options[index])) values.push(v.options[index]);
        });

        // Produto sem variantes ("Default Title") não precisa de seletor
        if (values.length === 1 && values[0] === 'Default Title') {
          this.selected[index] = values[0];
          return;
        }

        const group = document.createElement('div');
        group.className = 'kit-card__option';
        const label = document.createElement('p');
        label.className = 'kit-card__label';
        label.textContent = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
        const list = document.createElement('div');
        list.className = 'kit-card__values';

        values.forEach((value) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'kit-card__value';
          btn.textContent = value;
          btn.dataset.index = index;
          btn.dataset.value = value;
          btn.addEventListener('click', () => {
            this.selected[index] = this.selected[index] === value ? null : value;
            this.updateOptionStates();
            this.onChange();
          });
          list.appendChild(btn);
        });

        group.append(label, list);
        this.optionsEl.appendChild(group);
      });
    }

    // Um valor está disponível se existe variante em estoque com ele e com as outras escolhas já feitas
    isValueAvailable(index, value) {
      return this.product.variants.some(
        (v) =>
          v.available &&
          v.options[index] === value &&
          v.options.every((opt, j) => j === index || !this.selected[j] || this.selected[j] === opt)
      );
    }

    updateOptionStates() {
      this.optionsEl.querySelectorAll('.kit-card__value').forEach((btn) => {
        const index = Number(btn.dataset.index);
        const available = this.isValueAvailable(index, btn.dataset.value);
        btn.classList.toggle('is-active', this.selected[index] === btn.dataset.value);
        btn.classList.toggle('is-disabled', !available);
        btn.disabled = !available;
      });
    }

    get variant() {
      if (this.selected.some((value) => !value)) return null;
      return (
        this.product.variants.find((v) => v.available && v.options.every((opt, i) => opt === this.selected[i])) || null
      );
    }
  }

  class KitSelector extends HTMLElement {
    connectedCallback() {
      this.submit = this.querySelector('[data-kit-submit]');
      this.submitText = this.querySelector('[data-kit-submit-text]');
      this.error = this.querySelector('[data-kit-error]');
      this.cards = Array.from(this.querySelectorAll('[data-kit-card]')).map(
        (el) => new KitCard(el, () => this.update())
      );
      this.submit.addEventListener('click', () => this.addToCart());
      this.update();
    }

    update() {
      // Os cards chamam update() já na criação, antes de this.cards existir
      if (!this.cards) return;
      const ready = this.cards.length > 0 && this.cards.every((card) => card.variant);
      this.submit.disabled = !ready;
      this.submitText.textContent = ready ? 'Adicionar kit ao carrinho' : 'Selecione as opções do kit';
      this.showError('');
    }

    showError(message) {
      this.error.classList.toggle('d-none', !message);
      this.error.querySelector('span').textContent = message;
    }

    async addToCart() {
      const kitId = Date.now().toString(36);
      const properties = { Kit: this.dataset.kitTitle, _kit_id: kitId };

      // Agrupa peças iguais (ex.: 2 macaquinhos da mesma cor e tamanho)
      const quantities = new Map();
      this.cards.forEach((card) => {
        const id = card.variant.id;
        quantities.set(id, (quantities.get(id) || 0) + 1);
      });
      const items = Array.from(quantities, ([id, quantity]) => ({ id, quantity, properties }));

      this.submit.classList.add('with-loader--show');
      this.submit.querySelector('.spinner')?.classList.add('animating');
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        const data = await response.json();
        if (!response.ok) {
          this.showError(data.description || data.message || 'Não foi possível adicionar o kit ao carrinho.');
          return;
        }

        // O tema declara `const theme` (assets/global.js): ele existe como variável global,
        // mas não em window.theme, que é outro objeto vazio
        const themeObj = typeof theme !== 'undefined' ? theme : window.theme;
        if (document.getElementById('ajaxDrawer') && themeObj?.cart?.drawer) {
          themeObj.cart.drawer.openDrawer();
        } else {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch (error) {
        console.error(error);
        this.showError('Não foi possível adicionar o kit ao carrinho.');
      } finally {
        this.submit.classList.remove('with-loader--show');
        this.submit.querySelector('.spinner')?.classList.remove('animating');
      }
    }
  }

  customElements.define('kit-selector', KitSelector);
}
